<template>
  <section class="admin-games">
    <p v-if="!games && !message" class="admin-note">{{ t('page-loading') }}</p>
    <template v-if="games">
      <div v-if="chosen" class="admin-who">
        {{ t('admin-games-player', { name: chosen.displayName }) }}
        <button type="button" :aria-label="t('admin-games-player-clear')" :title="t('admin-games-player-clear')" @click="$emit('clear-player')">✕</button>
      </div>
      <div v-else class="admin-search">
        <input v-model="search" type="search" name="gameSearch" :placeholder="t('admin-games-search')" :aria-label="t('admin-games-search')" />
      </div>
      <div class="admin-filter" role="group">
        <button v-for="f in filters" :key="f.key" type="button" :data-filter="f.key" :class="{ on: filter === f.key }"
                :aria-pressed="filter === f.key ? 'true' : 'false'" @click="filter = f.key">{{ t(f.tag) }} {{ f.n }}</button>
      </div>
      <table v-if="shown.length">
        <tr><th>{{ t('admin-col-id') }}</th><th>{{ t('admin-col-status') }}</th><th class="moves">{{ t('admin-col-moves') }}</th><th>{{ t('admin-col-players') }}</th><th></th></tr>
        <tr v-for="g in shown" :key="g.id" :data-game="g.id" :class="{ 'is-empty': kind(g) === 'empty' }">
          <td class="num">{{ g.id }}</td>
          <td class="status"><span :class="['pill', kind(g)]">{{ kind(g) === 'empty' ? t('admin-status-empty') : statusText(g) }}</span></td>
          <td :class="['moves', { none: !g.moveCount }]">{{ t('admin-moves', { n: g.moveCount || 0 }) }}<span
            v-if="g.analysisState" :class="['analysis', g.analysisState]">{{ t(`admin-analysis-${g.analysisState}`) }}</span></td>
          <td class="players">{{ playersText(g) }}</td>
          <td class="acts">
            <template v-if="editing === g.id">
              <span class="inline">
                {{ t('color-black') }} <select v-model="black" name="black"><option v-for="u in users" :key="u.id" :value="u.id">{{ u.displayName }}</option></select>
                {{ t('color-white') }} <select v-model="white" name="white"><option v-for="u in users" :key="u.id" :value="u.id">{{ u.displayName }}</option></select>
                <button type="button" @click="savePlayers(g)">{{ t('button-save') }}</button>
                <button type="button" @click="editing = null">{{ t('dialog-cancel') }}</button>
              </span>
            </template>
            <template v-else-if="deleting === g.id">
              <span class="inline">
                <template v-if="deleteNeedsId">
                  {{ t('admin-delete-confirm', { id: g.id }) }} <input v-model="typedId" name="confirmId" size="6" />
                </template>
                <template v-else>{{ t('delete-game-confirm') }}</template>
                <button type="button" class="danger" @click="remove(g)">{{ t('button-confirm') }}</button>
                <button type="button" @click="deleting = null">{{ t('dialog-cancel') }}</button>
              </span>
            </template>
            <template v-else>
              <button v-if="!g.createdBy" type="button" @click="startEdit(g)">{{ t('admin-set-players') }}</button>
              <button type="button" class="danger" @click="startDelete(g)">{{ t('admin-delete-game') }}</button>
            </template>
          </td>
        </tr>
      </table>
      <p v-else class="admin-note">{{ t('admin-no-match') }}</p>
    </template>
    <p v-if="message" :class="message.cls">{{ message.text }}</p>
  </section>
</template>

<script>
import text from '@/text';
import { t, errorText, playersText } from '@/texts';
import { adminCall, onAdminChange, adminChanged } from '@/admin';

// The games tab: every game, newest first, narrowed by the search box or the chosen player (`player`,
// from the address) and by the status filter. A game in progress without moves is "empty"; under a
// finished game's moves, how far its analysis goes (analysisState from the server).
export default {
  name: 'AdminGames',
  props: { player: { type: Number, default: null } },
  data() {
    return { games: null, users: [], editing: null, deleting: null, deleteNeedsId: false,
             black: null, white: null, typedId: '', message: null, search: '', filter: 'all' };
  },
  computed: {
    chosen() {
      return this.player ? this.users.find(u => u.id === this.player) || null : null;
    },
    // The games the search or the chosen player lets through, before the status filter.
    matching() {
      if (!this.games) return [];
      if (this.chosen) return this.games.filter(g => (g.players || []).some(p => p.id === this.chosen.id));
      const q = this.search.trim().toLowerCase();
      if (!q) return this.games;
      return this.games.filter(g => String(g.id).includes(q)
        || (g.players || []).some(p => (p.displayName || '').toLowerCase().includes(q)));
    },
    filters() {
      const n = k => this.matching.filter(g => this.kind(g) === k).length;
      return [
        { key: 'all', tag: 'admin-filter-all', n: this.matching.length },
        { key: 'playing', tag: 'admin-filter-playing', n: n('playing') },
        { key: 'empty', tag: 'admin-filter-empty', n: n('empty') },
        { key: 'done', tag: 'admin-filter-done', n: n('done') },
      ];
    },
    shown() {
      return this.filter === 'all' ? this.matching : this.matching.filter(g => this.kind(g) === this.filter);
    },
  },
  async created() {
    this.stop = onAdminChange(this.load);
    await this.load();
  },
  beforeDestroy() {
    this.stop();
  },
  methods: {
    t, playersText,
    statusText(g) { return text.status(g); },
    kind(g) {
      if (g.status === 'completed') return 'done';
      return g.moveCount ? 'playing' : 'empty';
    },
    async load() {
      try {
        const [games, users] = await Promise.all([adminCall('GET', 'games/'), adminCall('GET', 'users')]);
        this.games = games.sort((a, b) => b.id - a.id);
        this.users = users;
        this.$emit('games', this.games);
      } catch (e) {
        this.show('warn', t('load-failed', { reason: errorText(e.msg || String(e)) }));
      }
    },
    show(cls, text) { this.message = { cls, text }; },
    startEdit(g) {
      this.message = null;
      this.deleting = null;
      this.editing = g.id;
      // Start at the current players; a colour nobody plays, or whose account is gone, is blank.
      const known = id => (this.users.some(u => u.id === id) ? id : null);
      const ids = (g.players || []).map(p => p.id);
      const black = g.setup && g.setup.blackPlayer;
      const white = ids.length === 1 ? ids[0] : ids.find(id => id !== black);
      this.black = black ? known(black) : null;
      this.white = black && white ? known(white) : null;
    },
    async savePlayers(g) {
      if (!this.black || !this.white) return this.show('warn', t('error-invalid-settings'));
      const players = [...new Set([this.black, this.white])].map(id => ({ id }));
      try {
        await adminCall('PATCH', `games/${g.id}`, { setup: { blackPlayer: this.black }, players });
        this.editing = null;
        await this.load();
        adminChanged(this.load);
        this.show('info', t('admin-saved'));
      } catch (e) {
        this.show('warn', errorText(e.msg || String(e)));
      }
    },
    async startDelete(g) {
      this.message = null;
      this.editing = null;
      this.typedId = '';
      try {
        const full = await adminCall('GET', `games/${g.id}`);
        this.deleteNeedsId = (full.moves || []).length > 0;
        this.deleting = g.id;
      } catch (e) {
        this.show('warn', errorText(e.msg || String(e)));
      }
    },
    async remove(g) {
      if (this.deleteNeedsId && this.typedId.trim() !== String(g.id)) return;
      try {
        await adminCall('DELETE', `games/${g.id}`);
        this.deleting = null;
        await this.load();
        adminChanged(this.load);
        this.show('info', t('admin-saved'));
      } catch (e) {
        this.show('warn', errorText(e.msg || String(e)));
      }
    },
  },
};
</script>
